import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, ChevronDown, Crown, GitBranch, Pencil, Plus, Settings2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useWorld } from "@/context/WorldContext";
import { isReviewer } from "@/lib/perms";
import { api, formatApiError } from "@/lib/api";

const ProfileForm = ({ initial, onSave, onCancel, busy, testId }) => {
  const [f, setF] = useState(initial);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3" data-testid={testId}>
      <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Profile name, e.g. Saltmere Coffee Shop AU" maxLength={80} required data-testid={`${testId}-name`} />
      <Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="What's different here? (optional)" maxLength={600} rows={3} data-testid={`${testId}-description`} />
      <div className="flex gap-2">
        <button disabled={busy || !f.name.trim()} className="rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-onaccent text-sm font-semibold px-5 h-9" data-testid={`${testId}-submit`}>{busy ? "Saving…" : "Save"}</button>
        {onCancel && <button type="button" onClick={onCancel} className="rounded-full border border-line px-4 h-9 text-sm text-slate-300">Cancel</button>}
      </div>
    </form>
  );
};

const ProfileRow = ({ p, onSaved, onDelete }) => {
  const [edit, setEdit] = useState(false);
  const save = async (f) => {
    try { await api.put(`/fanon/${p.id}`, f); await onSaved(); setEdit(false); toast.success("Profile updated"); } catch (e) { toast.error(formatApiError(e)); }
  };
  if (edit) return <li className="py-4"><ProfileForm initial={{ name: p.name, description: p.description }} onSave={save} onCancel={() => setEdit(false)} testId={`fanon-edit-${p.id}`} /></li>;
  return (
    <li className="py-4 flex items-start gap-3" data-testid={`fanon-row-${p.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-slate-100">{p.name}</p>
        {p.description && <p className="text-xs text-slate-400 mt-0.5">{p.description}</p>}
        <p className="text-[11px] font-code text-slate-500 mt-1">{p.page_count} entries{p.works?.length ? ` · linked to ${p.works.map((w) => w.title).join(", ")}` : ""}</p>
      </div>
      <button onClick={() => setEdit(true)} className="p-2 text-slate-400 hover:text-amber-300" title="Rename" data-testid={`fanon-edit-btn-${p.id}`}><Pencil className="w-4 h-4" /></button>
      <button onClick={() => onDelete(p)} className="p-2 text-slate-400 hover:text-rose-400" title="Delete" data-testid={`fanon-delete-btn-${p.id}`}><Trash2 className="w-4 h-4" /></button>
    </li>
  );
};

const ManageDialog = ({ open, onOpenChange, startCreating }) => {
  const { world, profiles, fanonLimit, reloadProfiles, rootPath, profile } = useWorld();
  const nav = useNavigate();
  const [creating, setCreating] = useState(startCreating);
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);
  const full = fanonLimit != null && profiles.length >= fanonLimit;
  const create = async (f) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/worlds/${world.id}/fanon`, f);
      await reloadProfiles(); toast.success(`"${data.name}" created with ${data.page_count} blank entries`);
      onOpenChange(false); nav(`${rootPath}/fanon/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try {
      await api.delete(`/fanon/${del.id}`); toast.success(`"${del.name}" deleted`);
      if (profile?.id === del.id) nav(rootPath);
      await reloadProfiles();
    } catch (e) { toast.error(formatApiError(e)); }
    setDel(null);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-panel border-line max-w-lg max-h-[85vh] overflow-y-auto" data-testid="fanon-dialog">
        <DialogTitle className="font-display text-3xl text-amber-50">Fanon profiles</DialogTitle>
        <DialogDescription className="text-sm text-slate-400">
          Each profile is its own version of {world.name}. Canon entries come across with their name, category and picture, ready for the fanon version. Anything you add or remove in a profile never touches canon.
        </DialogDescription>
        <ul className="divide-y divide-line" data-testid="fanon-list">
          {profiles.length === 0 && <li className="py-4 text-sm text-slate-500">No fanon profiles yet.</li>}
          {profiles.map((p) => <ProfileRow key={p.id} p={p} onSaved={reloadProfiles} onDelete={setDel} />)}
        </ul>
        {full ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-slate-300" data-testid="fanon-limit-note">
            <p className="flex items-center gap-2 text-amber-200"><Crown className="w-4 h-4" /> Free worlds get {fanonLimit} fanon profile.</p>
            <p className="mt-1 text-slate-400">{world.my_role === "owner" ? <>Upgrade to <Link to="/pricing" className="text-amber-300 hover:underline" data-testid="fanon-upgrade-link">Pro</Link> for as many as your fics need.</> : "The world owner can upgrade to Pro for more."}</p>
          </div>
        ) : creating ? (
          <ProfileForm initial={{ name: "", description: "" }} onSave={create} onCancel={() => setCreating(false)} busy={busy} testId="fanon-create-form" />
        ) : (
          <button onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-line hover:border-amber-500/50 text-sm text-slate-300 hover:text-amber-300 px-4 h-10 w-fit" data-testid="fanon-new-btn"><Plus className="w-4 h-4" /> New fanon profile</button>
        )}
        <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
          <AlertDialogContent className="bg-panel border-line" data-testid="fanon-delete-dialog">
            <AlertDialogTitle className="font-display text-2xl text-amber-50">Delete "{del?.name}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">All {del?.page_count} entries and relationships in this profile are deleted. Canon stays exactly as it is, and linked works are unlinked.</AlertDialogDescription>
            <AlertDialogFooter><AlertDialogCancel>Keep it</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="fanon-delete-confirm">Delete profile</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export const FanonSwitcher = () => {
  const { world, profiles, profile, rootPath, readOnly } = useWorld();
  const [dialog, setDialog] = useState(null);
  const manage = !readOnly && isReviewer(world);
  if (readOnly && !profiles.length) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 sm:px-3 py-1.5 text-xs transition-colors ${profile ? "border-violet-400/50 bg-violet-500/10 text-violet-200" : "border-line text-slate-300 hover:border-amber-500/40"}`} data-testid="fanon-switcher">
            <GitBranch className="w-3.5 h-3.5" /><span className="max-w-[9rem] truncate">{profile ? profile.name : "Canon"}</span><ChevronDown className="w-3 h-3 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 bg-panel border-line" data-testid="fanon-menu">
          <DropdownMenuLabel className="text-[10px] font-code uppercase tracking-widest text-slate-500">Version of this world</DropdownMenuLabel>
          <DropdownMenuItem asChild><Link to={rootPath} data-testid="fanon-menu-canon"><span className="flex-1">Canon</span>{!profile && <Check className="w-4 h-4 text-amber-400" />}</Link></DropdownMenuItem>
          {profiles.map((p) => (
            <DropdownMenuItem key={p.id} asChild><Link to={`${rootPath}/fanon/${p.id}`} data-testid={`fanon-menu-${p.id}`}><span className="flex-1 truncate">{p.name}</span>{profile?.id === p.id && <Check className="w-4 h-4 text-amber-400" />}</Link></DropdownMenuItem>
          ))}
          {manage && <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setDialog("create")} data-testid="fanon-menu-new"><Plus className="w-4 h-4" /> New fanon profile</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setDialog("manage")} data-testid="fanon-menu-manage"><Settings2 className="w-4 h-4" /> Manage profiles</DropdownMenuItem>
          </>}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialog && <ManageDialog key={dialog} open onOpenChange={(o) => !o && setDialog(null)} startCreating={dialog === "create"} />}
    </>
  );
};

export const FanonBanner = () => {
  const { profile, rootPath } = useWorld();
  if (!profile) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 md:px-6 py-2 text-xs bg-violet-500/10 border-b border-violet-400/30 text-violet-100" data-testid="fanon-banner">
      <GitBranch className="w-3.5 h-3.5 text-violet-300" />
      <span><span className="font-semibold">Fanon profile:</span> {profile.name}{profile.description ? ` · ${profile.description}` : ""}</span>
      <span className="text-violet-200/70">Entries here never appear in canon.</span>
      {profile.works?.length > 0 && (
        <span className="flex flex-wrap gap-1.5" data-testid="fanon-banner-works">
          {profile.works.map((w) => <Link key={w.id} to={`/writing/${w.id}`} className="rounded-full border border-violet-400/40 px-2 py-0.5 hover:bg-violet-500/20">{w.title}</Link>)}
        </span>
      )}
      <Link to={rootPath} className="ml-auto text-violet-200 hover:text-white underline-offset-2 hover:underline" data-testid="fanon-banner-canon">Back to canon</Link>
    </div>
  );
};
